import { useState, useEffect } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate, 
  useNavigate, 
  useLocation
} from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Construction from './components/Construction';
import Login from './components/Login';
import RequireAuth from './components/RequireAuth';
import Team from './components/Team';
import Security from './components/Security';

// AppRoutes component uses hooks that require being inside a Router
function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState('Dashboard');
  const [constructionTab, setConstructionTab] = useState('overview');
  const [selectedScope, setSelectedScope] = useState(null);
  
  useEffect(() => {
    // Parse the pathname to determine page, tab, and scope
    const pathname = location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    
    if (segments[0] === 'construction') {
      setCurrentPage('Construction');
      
      // Check for tab
      if (segments.length > 1) {
        if (segments[1] === 'overview' || segments[1] === 'budget' || segments[1] === 'settings') {
          setConstructionTab(segments[1]);
        }
        
        // Check for scope detail
        if (segments[1] === 'budget' && segments.length > 2) {
          const scopeIdentifier = decodeURIComponent(segments[2]);
          setSelectedScope(scopeIdentifier);
        } else {
          setSelectedScope(null);
        }
      } else {
        // Default to overview tab if only /construction is specified
        setConstructionTab('overview');
        setSelectedScope(null);
      }
    } else if (segments[0] === 'login') {
      // Do nothing for login page
    } else if (segments[0] === 'admin') {
      setCurrentPage('Admin');
      // If no subpath, redirect to team
      if (segments.length === 1 && pathname === '/admin') {
        navigate('/admin/team');
      }
    } else {
      setCurrentPage('Dashboard');
      setSelectedScope(null);
    }
  }, [location, navigate]);

  // Navigation helpers
  const navigateTo = {
    dashboard: () => navigate('/'),
    admin: {
      team: () => navigate('/admin/team'),
      security: () => navigate('/admin/security')
    },
    construction: {
      overview: () => navigate('/construction/overview'),
      budget: () => navigate('/construction/budget'),
      settings: () => navigate('/construction/settings'),
      scopeDetail: (shortName) => navigate(`/construction/budget/${encodeURIComponent(shortName)}`)
    }
  };

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route path="/" element={
        <RequireAuth requiredRole="read">
          <Layout currentPage={currentPage} navigateTo={navigateTo}>
            <Dashboard />
          </Layout>
        </RequireAuth>
      } />
      
      <Route path="/construction/*" element={
        <RequireAuth requiredRole="read">
          <Layout currentPage={currentPage} navigateTo={navigateTo}>
            <Construction 
              activeTab={constructionTab}
              selectedScope={selectedScope}
              navigateTo={navigateTo}
            />
          </Layout>
        </RequireAuth>
      } />
      
      <Route path="/admin" element={<Navigate to="/admin/team" replace />} />
      
      <Route path="/admin/team" element={
        <RequireAuth requiredRole="admin">
          <Layout currentPage="Admin" navigateTo={navigateTo}>
            <Team />
          </Layout>
        </RequireAuth>
      } />
      
      <Route path="/admin/security" element={
        <RequireAuth>
          <Layout currentPage="Admin" navigateTo={navigateTo}>
            <Security />
          </Layout>
        </RequireAuth>
      } />
      
      {/* Catch all route - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AuthProvider>
        <ThemeProvider>
          <AppRoutes />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;